import { useL10n, useTabsSyncedCallbackTrigger } from '@uplift/hooks';
import { Alert, AutoLogoutTrigger, ContextualNavigationLayout, Scheduler, useAlert } from '@uplift/ui-v2';
import { useCallback, useMemo, useState } from 'react';
import { Redirect, useHistory } from 'react-router';
import { useCanScheduleWithProvider } from '../../hooks/use-can-schedule-with-provider';
import { clientScheduleSessionRoute, ROUTING_CONFIG } from '@uplift/constants';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useAvailability, useProviderPublicMatchingProfile } from '@uplift/queries';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { CalendarDateService, DateObject } from '@uplift/services';
import { makeStyles } from 'tss-react/mui';
import { Loading } from '@uplift/pages';
import { ConfirmModal } from './components/confirm-modal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppointmentRecurring, AppointmentSlotDuration, createPsychiatryAppointment, createTherapyAppointment } from '@uplift/api';
import { ProviderMedicalRole } from '@uplift/types';
import { getSessionDuration } from './helpers';


export const ScheduleSessionPage: React.VFC = () => {
  const { providerId } = clientScheduleSessionRoute.useParams();
  const l10n = useL10n();
  const history = useHistory();
  const logoutTrigger = useTabsSyncedCallbackTrigger(AutoLogoutTrigger.logoutManual);
  const { canSchedule, isLoading } = useCanScheduleWithProvider(providerId);

  const handleBack = useCallback(() => {
    history.goBack();
  }, [ history ]);

  const handleLogout = useCallback(() => {
    logoutTrigger();
  }, [ logoutTrigger ]);

  if (!isLoading && !canSchedule) {
    return <Redirect to={ROUTING_CONFIG.dashboard} />;
  }

  return (
    <ContextualNavigationLayout
      headerProps={{
        onLeftButtonClick: handleBack,
        leftButtonText: l10n.navigation.back,
        onRightButtonClick: handleLogout,
        rightButtonText: l10n.navigation.logout,
      }}
    >
      {isLoading ? <Loading fixed /> : <ScheduleSession />}
    </ContextualNavigationLayout>
  );
};

const ScheduleSession = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const timezone = useTimezone();
  const me = useCurrentuser();
  const l10n = useL10n();
  const theme = useTheme();
  const [ isSlotUnavailable, setIsSlotUnavailable ] = useState(false);
  const handleError = async (error?: string) => {
    if (error === 'slotNoLongerAvailable') {
      await queryClient.invalidateQueries({ queryKey: [ 'availability-slots' ] });
      setIsSlotUnavailable(true);
      snackbar.error(l10n.schedule.createAppointment.errors.slotNoLongerAvailable.body);

      return;
    }

    setSelectedSlot(null);
    snackbar.error(error || 'An error occurred while completing your booking. Please try again.');
  };
  const createPsychAppointmentMutation = useMutation({
    mutationFn: createPsychiatryAppointment,
    onSuccess: async (data) => {
      if (data.error) {
        await handleError(data.error);
        return;
      }

      snackbar.success('Session Scheduled');
      history.push(ROUTING_CONFIG.dashboard);
    },
    onError: (error: Error) => handleError(error.message),
  });
  const createTherapyAppointmentMutation = useMutation({
    mutationFn: createTherapyAppointment,
    onSuccess: async (data) => {
      if (data.error) {
        await handleError(data.error);
        return;
      }
      snackbar.success('Session Scheduled');
      history.push(ROUTING_CONFIG.dashboard);
    },
    onError: (error: Error) => handleError(error.message),
  });
  const { classes } = useScheduleSessionStyles();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { providerId } = clientScheduleSessionRoute.useParams();
  const { data: provider } = useProviderPublicMatchingProfile(providerId);
  const snackbar = useAlert();
  const [ selectedSlot, setSelectedSlot ] = useState<DateObject | null>(null);
  const isPsychiatrist = provider?.user?.medicalRole === ProviderMedicalRole.psychiatrist;
  const slotDuration = isPsychiatrist ? AppointmentSlotDuration.thirty : AppointmentSlotDuration.sixty;
  const { slots: availabilitySlots } = useAvailability({
    providerIds: [ providerId ],
    patientId: me.id,
    bookingSlotDuration: slotDuration,
    bookingSource: 'patient_books_directly'
  });

  const slots = useMemo(() => {
    const squashedSlots = availabilitySlots
      .flatMap(x => x.slots)
      .reduce((acc, cur) => acc.add(cur), new Set<string>());

    return Array.from(squashedSlots).map(x => CalendarDateService.date(x).tz(timezone));
  }, [ availabilitySlots, timezone ]);

  const handleScheduleSession = useCallback((date: DateObject) => {
    setSelectedSlot(date);
  }, []);
  const handleDisabledClick = () => {
    snackbar.info(l10n.findPsychiatrist.tooFar, { preventDuplicate: true });
  };
  const handleClose = useCallback(async (confirm?: boolean) => {
    if (confirm && selectedSlot && provider) {
      try {
        if (isPsychiatrist) {
          await createPsychAppointmentMutation.mutateAsync({
            provider_id: providerId,
            patient_id: me.id,
            time_start: selectedSlot.utc().format(),
            duration: slotDuration,
            notes: '',
            timezone: timezone,
          });
        } else {
          await createTherapyAppointmentMutation.mutateAsync({
            provider_id: providerId,
            patient_id: me.id,
            time_start: selectedSlot.utc().format(),
            duration: slotDuration,
            notes: '',
            timezone: timezone,
            is_new_patient: false,
            recurring: AppointmentRecurring.none,
            is_working_time: false,
          });
        }
      } catch {
        // Error already handled by onError/onSuccess callbacks
      }
      return;
    }

    setSelectedSlot(null);
  }, [ createPsychAppointmentMutation, createTherapyAppointmentMutation, isPsychiatrist, me.id, provider, providerId, selectedSlot, slotDuration, timezone ]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      width={{ md: '690px' }}
      p={t => ({ xs: t.spacing(2), sm: t.spacing(4, 2), md: t.spacing(13, 2, 12) })}
    >
      <Box mb={isSlotUnavailable ? 1 : 5}>
        <Typography variant="h3" mb={1}>
          Please select a time for your session
        </Typography>
        <Typography variant="h4" fontWeight={400} color="darkSlate">
          {getSessionDuration(provider?.user?.medicalRole)} minute session
        </Typography>
      </Box>
      {isSlotUnavailable && (
        <Box>
          <Alert severity="info" title={l10n.matching.selectedProvider.rebookAlertTitle}>
            <span dangerouslySetInnerHTML={{ __html: l10n.matching.selectedProvider.rebookAlertContent }} />
          </Alert>
        </Box>
      )}
      <Box border={t => `1px solid ${t.palette.divider}`} borderRadius="4px" overflow="hidden">
        <Box borderBottom={t => `1px solid ${t.palette.divider}`} p={2} display="flex" alignItems="center" gap={2}>
          <Box
            component="img"
            src={provider?.avatarAsset.url}
            alt={provider?.nameDisplay.defaultDisplay}
            width={isDesktop ? 72 : 64}
            height={isDesktop ? 72 : 64}
            display="flex"
          />
          <Box>
            <Typography fontWeight={500}>
              {provider?.nameDisplay.defaultDisplay}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {provider?.licenses?.[0]?.role?.name}
            </Typography>
          </Box>
        </Box>
        <Scheduler
          dayParting
          timeslots={slots}
          withDatePicker={isDesktop}
          onScheduleSession={handleScheduleSession}
          daysAheadLimit={31}
          hideTimezoneSelect={isDesktop}
          onDisabledForwardClick={handleDisabledClick}
          classes={{
            datePickerContainer: classes.datePickerContainer
          }}
        />
      </Box>
      {selectedSlot && (
        <ConfirmModal
          appointmentTime={selectedSlot}
          provider={provider}
          isLoading={createPsychAppointmentMutation.isPending || createTherapyAppointmentMutation.isPending}
          onClose={handleClose}
        />
      )}
    </Box>
  );
};

const useScheduleSessionStyles = makeStyles()({
  datePickerContainer: {
    border: 0
  },
});
